import { districts } from '../data/districts';
/** ピン PNG の論理高さ */
const PIN_HEIGHT = 36;
/** ピン PNG の論理幅の半分 */
const PIN_HALF_WIDTH = 14.5;
/**
 * ポップアップ全体の上方向シフト量 (px)。
 * カードのフラットシャドウ(6px)による視覚的ずれを補正する。
 */
const POPUP_LIFT = 6;
export class Popup {
    /**
     * @param container - #popup-layer 要素（7区分のポップアップを子として持つ）
     * @param onNavigate - Tab/Shift+Tab で区を切り替えるときのコールバック
     * @param onModalOpen - モーダルを開くときのコールバック（タイトル・矢印クリック、Space キー）
     */
    constructor(container, onNavigate, onModalOpen = () => { }) {
        /** districtId → ポップアップのルート要素 */
        Object.defineProperty(this, "popups", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        /** districtId → ピン要素 */
        Object.defineProperty(this, "pinElements", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        /** districtId → カード要素 */
        Object.defineProperty(this, "cardElements", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        /** 全ピン表示中フラグ */
        Object.defineProperty(this, "allPinsVisible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'hidden'
        });
        Object.defineProperty(this, "activeDistrictId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /** id → district のルックアップ Map（find() の線形検索を回避） */
        Object.defineProperty(this, "_districtMap", {
            enumerable: false,
            configurable: true,
            writable: true,
            value: new Map(districts.map(d => [d.id, d]))
        });
        Object.defineProperty(this, "onNavigate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onModalOpen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * all-pins モードでピンにフォーカスが移ったときのコールバック。
         * map.js から setOnPinFocus() で登録し、mapView.panToDistrictKbd() を呼ぶ。
         */
        Object.defineProperty(this, "onPinFocus", {
            enumerable: false,
            configurable: true,
            writable: true,
            value: null
        });
        /** モーダル等が開いている間キーボードナビを抑制するフラグ */
        Object.defineProperty(this, "suppressKeyboard", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /**
         * キーボード操作でポップアップを遷移した直後に open-link へ
         * 自動フォーカスするためのフラグ。Enter でそのままモーダルへ遷移可能にする。
         */
        Object.defineProperty(this, "_focusOpenLinkOnExpand", {
            enumerable: false,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "onKeyDown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                if (this.suppressKeyboard)
                    return;
                // all-pins モード中はピン自身の keydown でハンドリングするため、
                // ここでは state が 'hidden' なら何もしない
                if (this.state === 'hidden' || !this.activeDistrictId)
                    return;
                if (e.key === 'Tab') {
                    // Tab / Shift+Tab で前後の区に移動する
                    e.preventDefault();
                    this.navigateDistrict(e.shiftKey ? -1 : 1, true);
                }
                else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    // 矢印キーでも前後の区に移動（マップ UI の慣例）
                    e.preventDefault();
                    this.navigateDistrict(1, true);
                }
                else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    this.navigateDistrict(-1, true);
                }
                else if (e.key === 'Home') {
                    // Home キーで最初の区へ
                    e.preventDefault();
                    this.navigateToIndex(0, true);
                }
                else if (e.key === 'End') {
                    // End キーで最後の区へ
                    e.preventDefault();
                    this.navigateToIndex(districts.length - 1, true);
                }
                else if ((e.key === ' ' || e.key === 'Enter') && this.state === 'expanded') {
                    // Space / Enter でモーダルを開く（カード展開時のみ）
                    // ただし、既にボタン（open-link 等）にフォーカスがある場合は
                    // ブラウザ標準の click を優先させるために preventDefault しない
                    const activeEl = document.activeElement;
                    if (activeEl && activeEl.closest && activeEl.closest('.popup__open-link')) {
                        return;
                    }
                    e.preventDefault();
                    const district = districts.find((d) => d.id === this.activeDistrictId);
                    if (district)
                        this.onModalOpen(district);
                }
                else if (e.key === 'Escape') {
                    this.hide();
                }
            }
        });
        this.onNavigate = onNavigate;
        this.onModalOpen = onModalOpen;
        // HTML に定義された各区のポップアップ要素を収集し、イベントを設定する
        for (const district of districts) {
            const el = container.querySelector(`[data-district-id="${district.id}"]`);
            if (!el)
                continue;
            el.setAttribute('aria-hidden', 'true');
            // inert で子孫要素をフォーカス不可にする（opacity:0 だけでは Tab が到達する）
            el.setAttribute('inert', '');
            this.popups.set(district.id, el);
            const pin = el.querySelector('.popup__pin');
            const card = el.querySelector('.popup__card');
            if (pin) {
                this.pinElements.set(district.id, pin);
                // a11y: ピンをキーボードで操作可能にする（all-pins モード時に tabindex=0 へ切替）
                pin.setAttribute('role', 'button');
                pin.setAttribute('tabindex', '-1');
                pin.setAttribute('aria-label', `${district.name} を選択`);
                // 操作ヘルプ（Tab/Arrow/Home/End/Escape）を初回フォーカス時に読み上げ
                if (document.getElementById('map-keyboard-help')) {
                    pin.setAttribute('aria-describedby', 'map-keyboard-help');
                }
                // ピンアクション：全区で「移動 + モーダル直接表示」に統一
                const activatePin = () => {
                    this.onNavigate(district, true);
                    // 同一クリック内の他処理との競合を避けるため、次フレームでモーダルを開く
                    requestAnimationFrame(() => {
                        this.onModalOpen(district);
                    });
                };
                pin.addEventListener('pointerdown', (e) => {
                    // pointerup をピンに確実に届けるためにキャプチャする。
                    // preventDefault でポインター操作後の互換 click 生成を抑制し、
                    // モーダルリンクへのゴーストクリックを防ぐ。
                    e.preventDefault();
                    pin.setPointerCapture(e.pointerId);
                });
                pin.addEventListener('pointerup', (e) => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    activatePin();
                });
                // Enter / Space でピンを選択（スクロール抑制のため preventDefault）
                // stopPropagation で document の keydown への二重発火（モーダルまで一気に開く不具合）を防ぐ
                pin.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        activatePin();
                    }
                });
                // a11y: Tab でフォーカスが移ったとき、地図をパンしてピンを画面内に収める
                pin.addEventListener('focus', () => {
                    if (this.allPinsVisible) {
                        this.onPinFocus?.(district);
                    }
                });
            }
            if (card) {
                this.cardElements.set(district.id, card);
            }
            // タイトル＋矢印リンクのクリックでモーダルを開く
            const openLink = el.querySelector('.popup__open-link');
            if (openLink) {
                // a11y: スクリーンリーダーにモーダル（ダイアログ）を開くボタンだと伝える
                openLink.setAttribute('aria-haspopup', 'dialog');
                openLink.addEventListener('click', () => {
                    if (this.activeDistrictId === district.id) {
                        this.onModalOpen(district);
                    }
                });
            }
        }
        // キーボードナビゲーション
        document.addEventListener('keydown', this.onKeyDown);
    }
    /**
     * 指定した区のポップアップを表示する。
     * @param showExpanded - true のときピンアニメーションを省略してカードを直接表示する
     */
    show(district, screenCenter, showExpanded = false) {
        const el = this.popups.get(district.id);
        if (!el)
            return;
        // 全ピン表示中はピンを残したままカードを追加表示
        if (this.allPinsVisible) {
            el.classList.remove('popup--pins-only');
        }
        this.activeDistrictId = district.id;
        this.state = showExpanded ? 'expanded' : 'pin';
        this.applyState();
        this.position(screenCenter);
        el.setAttribute('aria-hidden', 'false');
        el.removeAttribute('inert');
        el.classList.remove('popup--hidden');
        requestAnimationFrame(() => el.classList.add('popup--visible'));
    }
    /** ピン状態からカード展開状態に遷移する */
    expand() {
        if (this.state !== 'pin')
            return;
        this.state = 'expanded';
        this.applyState();
    }
    /** ポップアップを非表示にする */
    hide() {
        if (!this.activeDistrictId)
            return;
        const el = this.popups.get(this.activeDistrictId);
        const pin = this.pinElements.get(this.activeDistrictId);
        const card = this.cardElements.get(this.activeDistrictId);
        // 全ピン表示中: カードだけ閉じてピン表示に戻す
        if (this.allPinsVisible) {
            if (card) card.classList.remove('popup__card--visible');
            if (el) el.classList.remove('popup--card-expanded');
            if (pin) {
                pin.classList.remove('popup__pin--hidden', 'popup__pin--rise');
                if (!pin.classList.contains('popup__pin--drop')) {
                    pin.classList.add('popup__pin--drop');
                }
            }
            if (el) el.classList.add('popup--pins-only');
            this.state = 'hidden';
            this.activeDistrictId = null;
            return;
        }
        if (el) {
            el.setAttribute('aria-hidden', 'true');
            el.setAttribute('inert', '');
            el.classList.remove('popup--visible', 'popup--card-expanded');
            el.classList.add('popup--hidden');
        }
        if (pin) {
            pin.classList.remove('popup__pin--drop', 'popup__pin--rise');
            pin.classList.add('popup__pin--hidden');
        }
        if (card)
            card.classList.remove('popup__card--visible');
        this.state = 'hidden';
        this.activeDistrictId = null;
    }
    /**
     * ポップアップの表示位置を更新する。
     */
    position(screenCenter) {
        if (!this.activeDistrictId)
            return;
        const el = this.popups.get(this.activeDistrictId);
        if (!el)
            return;
        el.style.left = `${screenCenter.x - PIN_HALF_WIDTH}px`;
        el.style.top = `${screenCenter.y - PIN_HEIGHT - POPUP_LIFT}px`;
    }
    isVisible() {
        return this.state !== 'hidden';
    }
    getActiveDistrict() {
        if (!this.activeDistrictId)
            return null;
        return this._districtMap.get(this.activeDistrictId) ?? null;
    }
    /** 現在アクティブなポップアップのルート DOM 要素を返す */
    getElement() {
        if (!this.activeDistrictId)
            return null;
        return this.popups.get(this.activeDistrictId) ?? null;
    }
    /**
     * all-pins モードでピンにフォーカスが移ったときに呼ばれるコールバックを登録する。
     * map.js から `mapView.panToDistrictKbd(district)` を渡すことで、
     * Tab ナビゲーション時に地図が自動パンするようになる。
     * @param callback - (district) => void
     */
    setOnPinFocus(callback) {
        this.onPinFocus = callback;
    }
    /** 現在の state に合わせてピンとカードの表示を切り替える */
    applyState() {
        if (!this.activeDistrictId)
            return;
        const el = this.popups.get(this.activeDistrictId);
        const pin = this.pinElements.get(this.activeDistrictId);
        const card = this.cardElements.get(this.activeDistrictId);
        if (this.state === 'pin') {
            if (pin) {
                pin.classList.remove('popup__pin--hidden', 'popup__pin--rise', 'popup__pin--drop');
                void pin.offsetWidth; // reflow でアニメーションをリセット
                pin.classList.add('popup__pin--drop');
            }
            if (card)
                card.classList.remove('popup__card--visible');
            if (el)
                el.classList.remove('popup--card-expanded');
        }
        else if (this.state === 'expanded') {
            // 全ピン表示中はピンを残したまま表示
            if (pin && !this.allPinsVisible) {
                const fromPin = pin.classList.contains('popup__pin--drop');
                pin.classList.remove('popup__pin--drop');
                if (fromPin) {
                    pin.classList.add('popup__pin--rise');
                    pin.addEventListener('animationend', () => {
                        pin.classList.remove('popup__pin--rise');
                        pin.classList.add('popup__pin--hidden');
                    }, { once: true });
                }
                else {
                    pin.classList.remove('popup__pin--rise');
                    pin.classList.add('popup__pin--hidden');
                }
            }
            if (card) {
                // カードのアニメーション初期状態をリセットしてから表示
                card.classList.remove('popup__card--visible');
                void card.offsetWidth;
                card.classList.add('popup__card--visible');
            }
            if (el)
                el.classList.add('popup--card-expanded');
            // キーボード操作由来の展開であれば open-link にフォーカス
            if (this._focusOpenLinkOnExpand && el) {
                this._focusOpenLinkOnExpand = false;
                const openLink = el.querySelector('.popup__open-link');
                if (openLink) {
                    // カードのフェードインと同期させるため rAF 後にフォーカス
                    requestAnimationFrame(() => {
                        openLink.focus({ preventScroll: true });
                    });
                }
            }
        }
        else {
            if (pin) {
                pin.classList.add('popup__pin--hidden');
                pin.classList.remove('popup__pin--drop', 'popup__pin--rise');
            }
            if (card)
                card.classList.remove('popup__card--visible');
            if (el)
                el.classList.remove('popup--card-expanded');
        }
    }
    // ── 全ピン一斉表示 ────────────────────────────────────
    /**
     * 全区のピンを同時にドロップアニメーション表示する。
     * @param getScreenCenter - (district) => {x, y} でピン位置を取得
     */
    showAllPins(getScreenCenter) {
        this.allPinsVisible = true;
        for (const district of districts) {
            const el = this.popups.get(district.id);
            const pin = this.pinElements.get(district.id);
            const card = this.cardElements.get(district.id);
            if (!el || !pin) continue;
            // カードは非表示、ピンのみ表示
            if (card) card.classList.remove('popup__card--visible');
            el.classList.remove('popup--card-expanded');
            pin.classList.remove('popup__pin--hidden', 'popup__pin--rise', 'popup__pin--drop');
            void pin.offsetWidth;
            pin.classList.add('popup__pin--drop');
            // a11y: all-pins モード中はピンを Tab でフォーカス可能にする
            pin.setAttribute('tabindex', '0');
            // ポップアップを可視化して位置設定
            el.setAttribute('aria-hidden', 'false');
            el.removeAttribute('inert');
            el.classList.remove('popup--hidden');
            el.classList.add('popup--visible', 'popup--pins-only');
            const center = getScreenCenter(district);
            el.style.left = `${center.x - PIN_HALF_WIDTH}px`;
            el.style.top = `${center.y - PIN_HEIGHT - POPUP_LIFT}px`;
        }
    }
    /**
     * マップ変形時に全ピンの位置を追従更新する。
     * @param getScreenCenter - (district) => {x, y}
     */
    positionAllPins(getScreenCenter) {
        if (!this.allPinsVisible) return;
        for (const district of districts) {
            const el = this.popups.get(district.id);
            if (!el) continue;
            const center = getScreenCenter(district);
            el.style.left = `${center.x - PIN_HALF_WIDTH}px`;
            el.style.top = `${center.y - PIN_HEIGHT - POPUP_LIFT}px`;
        }
    }
    /** 全ピンを非表示にする */
    hideAllPins() {
        this.allPinsVisible = false;
        for (const district of districts) {
            const el = this.popups.get(district.id);
            const pin = this.pinElements.get(district.id);
            if (!el || !pin) continue;
            el.setAttribute('aria-hidden', 'true');
            el.setAttribute('inert', '');
            el.classList.remove('popup--visible', 'popup--pins-only');
            el.classList.add('popup--hidden');
            pin.classList.remove('popup__pin--drop', 'popup__pin--rise');
            pin.classList.add('popup__pin--hidden');
            // a11y: all-pins モード解除時はピンのフォーカス対象化も解除
            pin.setAttribute('tabindex', '-1');
        }
    }
    /**
     * direction +1 = 次の区、-1 = 前の区 へパン＆ポップアップ切り替え
     * @param focusOpenLink キーボード由来の場合 true を渡すと遷移後に open-link にフォーカスする
     */
    navigateDistrict(direction, focusOpenLink = false) {
        if (!this.activeDistrictId)
            return;
        const currentIndex = districts.findIndex((d) => d.id === this.activeDistrictId);
        const nextDistrict = districts[(currentIndex + direction + districts.length) % districts.length];
        if (focusOpenLink) this._focusOpenLinkOnExpand = true;
        // ナビゲーション時はピンをスキップしてカードを直接表示する
        this.onNavigate(nextDistrict, true);
    }
    /**
     * 指定したインデックスの区にジャンプする（Home / End キー用）
     * @param focusOpenLink キーボード由来の場合 true を渡すと遷移後に open-link にフォーカスする
     */
    navigateToIndex(index, focusOpenLink = false) {
        if (index < 0 || index >= districts.length) return;
        const target = districts[index];
        // 既に同じ区が開いている場合は何もしない
        if (this.activeDistrictId === target.id) return;
        if (focusOpenLink) this._focusOpenLinkOnExpand = true;
        this.onNavigate(target, true);
    }
    /** 最初の表示可能なピンにフォーカスを移動（all-pins モード用のエントリポイント） */
    focusFirstPin() {
        if (!this.allPinsVisible) return false;
        for (const district of districts) {
            const pin = this.pinElements.get(district.id);
            if (pin) {
                pin.focus({ preventScroll: true });
                return true;
            }
        }
        return false;
    }
}
Object.defineProperty(Popup, "CARD_HEIGHT", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 100
});
Object.defineProperty(Popup, "PIN_HEIGHT", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: PIN_HEIGHT
});
Object.defineProperty(Popup, "POPUP_WIDTH", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 240
});
